import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Eye, Play } from "lucide-react";
import { catLabel, catAccent, timeAgo } from "@/lib/api";
import { WhatsAppShareIcon } from "@/components/WhatsAppShare";

export const ArticleCard = ({ article, big = false, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
  >
    <Link
      to={`/article/${article.id}`}
      data-testid={`article-card-${article.id}`}
      className="group block h-full bg-[#15151A] border border-white/[0.08] hover:border-[#FFD400]/60 transition-[border-color,transform] duration-300 hover:-translate-y-1"
    >
      <div className={`relative overflow-hidden ${big ? "aspect-[16/9]" : "aspect-[16/10]"}`}>
        {article.youtube_video_id ? (
          <>
            <img
              src={article.image_url}
              alt=""
              aria-hidden
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-50"
            />
            <img
              src={article.image_url}
              alt={article.title}
              loading="lazy"
              className="relative w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
            />
          </>
        ) : (
          <img
            src={article.image_url}
            alt={article.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        )}
        {article.youtube_video_id && (
          <span
            data-testid={`article-card-play-${article.id}`}
            className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors duration-300 group-hover:bg-black/10"
          >
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#FF375F] text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play size={20} fill="currentColor" className="ml-0.5" />
            </span>
          </span>
        )}
        <span className="absolute top-3 right-3 z-10">
          <WhatsAppShareIcon article={article} />
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <span
            className="font-mono-meta text-[10px] font-semibold uppercase tracking-widest px-2 py-1"
            style={{ color: catAccent(article.category), border: `1px solid ${catAccent(article.category)}40` }}
          >
            {catLabel(article.category)}
          </span>
          <span className="font-mono-meta text-[10px] text-slate-500 flex items-center gap-1">
            <Clock size={10} /> {timeAgo(article.created_at)}
          </span>
        </div>
        <h3 className={`font-editorial font-bold leading-snug text-white group-hover:text-[#FFD400] transition-colors duration-200 ${big ? "text-xl sm:text-2xl" : "text-lg"}`}>
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{article.summary}</p>
        <div className="mt-4 flex items-center justify-between font-mono-meta text-[10px] text-slate-500 uppercase tracking-wider">
          <span>{article.author}</span>
          <span className="flex items-center gap-1">
            <Eye size={10} /> {article.views.toLocaleString("hi-IN")}
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);
